import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { learningService } from "../../../api/learningService";
import Badge from "../../../components/ui/Badge";
import Field from "../../../components/ui/Field";
import Modal from "../../../components/ui/Modal";
import { useToast } from "../../../components/ui/useToast";
import { getErrorMessage } from "../../../utils/apiError";
import { formatManilaDateTime } from "../../../utils/manilaTime";
import { cx } from "../../../utils/classNames";
import {
  formatLearningHours,
  formatSeminarWhen,
  getSeminarState,
  LEARNING_KEY,
  PARTICIPANT_SOURCE_LABELS,
  PARTICIPANT_STATUS,
  SEMINAR_MODE,
  SEMINAR_TYPE_LABELS,
} from "../learningUtils";
import { SkillChips } from "./SeminarCard";
import ui from "../../../components/ui/ui.module.css";
import styles from "../Learning.module.css";

// Seminar details with register / withdraw. `seminar` (from the list) is
// shown right away; the detail call refreshes seats and my status.
const SeminarDetailDrawer = ({ seminar: initial, onClose }) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [LEARNING_KEY, "seminar", initial.id],
    queryFn: () => learningService.getMySeminar(initial.id),
    placeholderData: initial,
  });
  const seminar = query.data ?? initial;

  const onDone = (message) => () => {
    toast.success(message);
    queryClient.invalidateQueries({ queryKey: [LEARNING_KEY] });
  };
  const onError = (fallback) => (error) =>
    toast.error(getErrorMessage(error, fallback));

  const enrollMutation = useMutation({
    mutationFn: () => learningService.enroll(seminar.id),
    onSuccess: onDone("You're registered."),
    onError: onError("Unable to register."),
  });

  const withdrawMutation = useMutation({
    mutationFn: () => learningService.withdraw(seminar.id),
    onSuccess: () => {
      onDone("Registration withdrawn.")();
      onClose();
    },
    onError: onError("Unable to withdraw."),
  });

  const state = getSeminarState(seminar);
  const mode = SEMINAR_MODE[seminar.mode];
  const my = seminar.my;
  const myStatus = my && PARTICIPANT_STATUS[my.status];
  const isBusy = enrollMutation.isPending || withdrawMutation.isPending;

  const footer =
    seminar.canEnroll || my?.canWithdraw ? (
      <>
        <button
          type="button"
          className={cx(ui.button, ui.secondary)}
          onClick={onClose}
        >
          Close
        </button>
        {seminar.canEnroll ? (
          <button
            type="button"
            className={cx(ui.button, ui.primary)}
            onClick={() => enrollMutation.mutate()}
            disabled={isBusy || seminar.seatsLeft === 0}
          >
            {enrollMutation.isPending
              ? "Registering…"
              : seminar.seatsLeft === 0
                ? "Full"
                : "Register"}
          </button>
        ) : (
          <button
            type="button"
            className={cx(ui.button, ui.dangerOutline)}
            onClick={() => withdrawMutation.mutate()}
            disabled={isBusy}
          >
            {withdrawMutation.isPending
              ? "Withdrawing…"
              : "Withdraw registration"}
          </button>
        )}
      </>
    ) : null;

  return (
    <Modal
      title={seminar.title}
      variant="drawer"
      onClose={onClose}
      footer={footer}
    >
      <div className="d-flex flex-wrap gap-1 mb-3">
        <Badge tone={state.tone}>{state.label}</Badge>
        {myStatus && <Badge tone={myStatus.tone}>{myStatus.label}</Badge>}
      </div>

      {seminar.status === "CANCELLED" && (
        <div className={cx(ui.alert, ui.alertDanger, "mb-3")}>
          Cancelled{seminar.cancelReason ? `: ${seminar.cancelReason}` : "."}
        </div>
      )}

      <div className={styles.detailGrid}>
        <Field label="When">
          {formatSeminarWhen(seminar.startAt, seminar.endAt)}
        </Field>
        <Field label="Type">
          {SEMINAR_TYPE_LABELS[seminar.type] ?? seminar.type} ·{" "}
          {mode?.label ?? seminar.mode}
        </Field>
        <Field label="Hours">
          {seminar.hours != null ? formatLearningHours(seminar.hours) : null}
        </Field>
        <Field label="Facilitator">{seminar.facilitator}</Field>
        <Field label="Venue">{seminar.venue}</Field>
        <Field label="Branch">{seminar.branchName ?? "All branches"}</Field>
        {seminar.seatsLeft != null && (
          <Field label="Seats left">{String(seminar.seatsLeft)}</Field>
        )}
        {my && (
          <Field label="How you joined">
            {PARTICIPANT_SOURCE_LABELS[my.source] ?? my.source}
          </Field>
        )}
      </div>

      {seminar.meetingLink && seminar.status !== "CANCELLED" && (
        <a
          href={seminar.meetingLink}
          target="_blank"
          rel="noopener noreferrer"
          className={cx(ui.button, ui.secondary, "mb-3")}
        >
          <i className="bi bi-camera-video"></i>
          Join meeting
        </a>
      )}

      {seminar.description && (
        <>
          <div className={ui.fieldLabel}>About</div>
          <p className={styles.description}>{seminar.description}</p>
        </>
      )}

      {seminar.skills?.length > 0 && (
        <>
          <div className={cx(ui.fieldLabel, "mb-1")}>
            Skills you earn by attending
          </div>
          <SkillChips skills={seminar.skills} />
        </>
      )}

      {my?.remarks && (
        <div className={cx(ui.alert, ui.alertGray, "mt-3")}>
          HR remarks: {my.remarks}
          {my.markedAt && ` (${formatManilaDateTime(my.markedAt)})`}
        </div>
      )}

      {my && my.source === "ASSIGNED" && my.status === "PENDING" && (
        <p className={cx(ui.hint, "mt-3 mb-0")}>
          HR assigned you to this seminar. Ask HR if you can't attend.
        </p>
      )}
    </Modal>
  );
};

export default SeminarDetailDrawer;
